import type React from "react";

export interface IModal {
  title: string;
  description?: string;
  cancelButton?: {
    label: string;
    onClick?: (e: React.MouseEvent) => void;
  };
  confirmButton?: {
    label: string;
    onClick?: (e: React.MouseEvent) => void;
  };
  children?: React.ReactNode;
  progress?: boolean;
  closeButton?: boolean;
  isCaptcha?: boolean;
  isLarge?: boolean;
  hasButton?: boolean;
}

export interface ILayoutState {
  isSearchOpen: boolean;
  isCartOpen: boolean;
  isLoginOpen: boolean;
  isModalOpen: boolean;
  isHeaderOpen: boolean;
  isShowFooter: boolean;
  modal: IModal;
  loginModalProps: {
    redirectUrl: string;
    callback: () => void;
  };
  toast: {
    message: string;
    type: string;
    isTransparent: boolean;
  };
}

const initialStateLayout: ILayoutState = {
  isSearchOpen: false,
  isCartOpen: false,
  isLoginOpen: false,
  isModalOpen: false,
  isHeaderOpen: false,
  isShowFooter: true,
  modal: {
    title: "",
    description: "",
    // cancelButton: {
    //   label: 'Cancel',
    //   onClick: () => {},
    // },
    confirmButton: {
      label: "Okay",
      onClick: () => {},
    },
    progress: false,
  },
  loginModalProps: {
    redirectUrl: "",
    callback: () => {},
  },
  toast: {
    message: "",
    type: "",
    isTransparent: true,
  },
};

const LAYOUT_OPEN_CART = "LAYOUT/OPEN_CART" as const;
const LAYOUT_CLOSE_CART = "LAYOUT/CLOSE_CART" as const;

const LAYOUT_OPEN_SEARCH = "LAYOUT/OPEN_SEARCH" as const;
const LAYOUT_CLOSE_SEARCH = "LAYOUT/CLOSE_SEARCH" as const;

const LAYOUT_OPEN_LOGIN = "LAYOUT/OPEN_LOGIN" as const;
const LAYOUT_CLOSE_LOGIN = "LAYOUT/CLOSE_LOGIN" as const;

const LAYOUT_OPEN_MODAL = "LAYOUT/OPEN_MODAL" as const;
const LAYOUT_CLOSE_MODAL = "LAYOUT/CLOSE_MODAL" as const;
const LAYOUT_SET_MODAL_PROGRESS = "LAYOUT/SET_MODAL_PROGRESS" as const;

const LAYOUT_OPEN_HEADER = "LAYOUT_OPEN_HEADER" as const;
const LAYOUT_CLOSE_HEADER = "LAYOUT_CLOSE_HEADER" as const;

const LAYOUT_OPEN_TOAST = "LAYOUT/OPEN_TOAST" as const;
const LAYOUT_HIDE_TOAST = "LAYOUT/HIDE_TOAST" as const;
const LAYOUT_CLOSE_TOAST = "LAYOUT/CLOSE_TOAST" as const;

const LAYOUT_SHOW_FOOTER = "LAYOUT/LAYOUT_SHOW_FOOTER" as const;

export const layout = (state = initialStateLayout, action: TLayoutAction) => {
  switch (action.type) {
    case LAYOUT_OPEN_SEARCH:
      return {
        ...state,
        isSearchOpen: action.isSearchOpen,
      };
    case LAYOUT_CLOSE_SEARCH:
      return {
        ...state,
        isSearchOpen: action.isSearchOpen,
      };
    case LAYOUT_OPEN_CART:
      return {
        ...state,
        isCartOpen: action.isCartOpen,
      };
    case LAYOUT_CLOSE_CART:
      return {
        ...state,
        isCartOpen: action.isCartOpen,
      };
    case LAYOUT_OPEN_LOGIN:
      return {
        ...state,
        isLoginOpen: true,
        loginModalProps: {
          ...state.loginModalProps,
          ...action.loginModalProps,
        },
      };
    case LAYOUT_CLOSE_LOGIN:
      return {
        ...state,
        isLoginOpen: false,
        modal: {
          ...initialStateLayout.modal,
        },
      };
    case LAYOUT_OPEN_MODAL:
      return {
        ...state,
        isModalOpen: true,
        modal: {
          ...state.modal,
          ...action.modal,
        },
      };
    case LAYOUT_CLOSE_MODAL:
      return {
        ...state,
        isModalOpen: false,
        modal: {
          ...initialStateLayout.modal,
        },
      };
    case LAYOUT_SET_MODAL_PROGRESS:
      return {
        ...state,
        modal: {
          ...state.modal,
          progress: action.progress,
        },
      };
    case LAYOUT_OPEN_HEADER:
      return {
        ...state,
        isHeaderOpen: true,
      };
    case LAYOUT_CLOSE_HEADER:
      return {
        ...state,
        isHeaderOpen: false,
      };
    case LAYOUT_SHOW_FOOTER:
      return {
        ...state,
        isShowFooter: action.isShowFooter,
      };
    case LAYOUT_OPEN_TOAST:
      return {
        ...state,
        toast: {
          message: action.message,
          type: action.toastType,
          isTransparent: false,
        },
      };
    case LAYOUT_HIDE_TOAST:
      return {
        ...state,
        toast: {
          message: action.message,
          type: action.toastType,
          isTransparent: true,
        },
      };
    case LAYOUT_CLOSE_TOAST:
      return {
        ...state,
        toast: {
          ...initialStateLayout.toast,
        },
      };
    default:
      return state;
  }
};

// 액션 타입 정의
type TLayoutAction =
  | ReturnType<typeof openModal>
  | ReturnType<typeof closeModal>
  | ReturnType<typeof setModalProgress>
  | ReturnType<typeof openLogin>
  | ReturnType<typeof closeLogin>
  | ReturnType<typeof openHeader>
  | ReturnType<typeof closeHeader>
  | ReturnType<typeof showFooter>
  | ReturnType<typeof openSearch>
  | ReturnType<typeof closeSearch>
  | ReturnType<typeof openCart>
  | ReturnType<typeof closeCart>
  | ReturnType<typeof openToast>
  | ReturnType<typeof hideToast>
  | ReturnType<typeof closeToast>;

const openCart = () => ({ type: LAYOUT_OPEN_CART, isCartOpen: true });
const closeCart = () => ({ type: LAYOUT_CLOSE_CART, isCartOpen: false });
const openSearch = () => ({ type: LAYOUT_OPEN_SEARCH, isSearchOpen: true });
const closeSearch = () => ({ type: LAYOUT_CLOSE_SEARCH, isSearchOpen: false });
const openLogin = (loginModalProps: { redirectUrl: string; callback?: (...args: any) => void }) => ({
  type: LAYOUT_OPEN_LOGIN,
  loginModalProps,
});
const closeLogin = () => ({ type: LAYOUT_CLOSE_LOGIN, loginModalProps: {} });
const openModal = (modal: IModal) => ({ type: LAYOUT_OPEN_MODAL, modal });
const closeModal = () => ({ type: LAYOUT_CLOSE_MODAL });
const setModalProgress = (progress: boolean) => ({
  type: LAYOUT_SET_MODAL_PROGRESS,
  progress,
});
const openHeader = () => ({ type: LAYOUT_OPEN_HEADER });
const closeHeader = () => ({ type: LAYOUT_CLOSE_HEADER });

const showFooter = (isShowFooter: boolean) => ({ type: LAYOUT_SHOW_FOOTER, isShowFooter });

const openToast = ({ message, toastType }: { message: string; toastType: string }) => ({
  type: LAYOUT_OPEN_TOAST,
  message,
  toastType,
});
const hideToast = ({ message, toastType }: { message: string; toastType: string }) => ({
  type: LAYOUT_HIDE_TOAST,
  message,
  toastType,
});
const closeToast = () => ({ type: LAYOUT_CLOSE_TOAST });

export const layoutActionCreator = {
  openModal,
  closeModal,
  setModalProgress,
  openLogin,
  closeLogin,
  openHeader,
  closeHeader,
  showFooter,
  openSearch,
  closeSearch,
  openCart,
  closeCart,
  openToast,
  hideToast,
  closeToast,
};
